"use client"

import React, { useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import { initiateForgotPasswordAction } from "@/actions/forgotPasswordActions"
import { zodResolver } from "@hookform/resolvers/zod"
import { LoaderCircle } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { FormControl, FormField, FormItem, FormMessage, Form as RHFForm } from "@/components/ui/components"
import { Button } from "@/components/ui/components/button"
import { Input } from "@/components/ui/components/input"
import { cn } from "@/components/ui/helpers/utils"
import AccountBackLinkClient from "@/components/account/AccountBackLinkClient"
import { FormRow } from "@/components/account/shared/FormRow"
import { UbAccountForgotpasswordViewStoryblok } from "@/components/storyblok/component-types"

const makeSchema = (blok?: UbAccountForgotpasswordViewStoryblok) =>
  z.object({
    email: z
      .string()
      .min(1, { message: blok?.label_email_required ?? "" })
      .email({ message: blok?.label_invalid_email ?? "" }),
  })

type FormValues = { email: string }

interface AuthInitiateForgotPasswordClientProps {
  blok: UbAccountForgotpasswordViewStoryblok
  initialTopContent: React.ReactNode
  initialBottomContent: React.ReactNode
  emailSentContent: React.ReactNode
}

export default function AuthInitiateForgotPasswordClient({
  blok,
  initialTopContent,
  initialBottomContent,
  emailSentContent,
}: AuthInitiateForgotPasswordClientProps) {
  const [isPending, startTransition] = useTransition()
  const [submitted, setSubmitted] = useState(false)
  const router = useRouter()

  const form = useForm<FormValues>({
    resolver: zodResolver(makeSchema(blok)),
    defaultValues: { email: "" },
    mode: "onBlur",
  })

  async function onSubmit(values: FormValues) {
    startTransition(async () => {
      try {
        const res = await initiateForgotPasswordAction(values.email)
        if (!res.success) {
          const msg =
            res.error === "invalid_email" ? (blok?.label_invalid_email ?? "") : (blok?.label_submit_error ?? "")
          toast.error(msg)
          return
        }
        toast.success(blok?.label_submit_success ?? "")
        setSubmitted(true)
      } catch (e) {
        toast.error(blok?.label_submit_error ?? "")
      }
    })
  }

  const { control, handleSubmit } = form

  return (
    <div className={"mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8"}>
      <AccountBackLinkClient label={blok?.label_common_back} />
      <div className="relative max-w-prose">
        <div className={cn("absolute", { hidden: !submitted })}>{emailSentContent}</div>
        <div className={cn("flex flex-col gap-2 lg:gap-6", { invisible: submitted })}>
          <div className={"flex flex-col gap-2"}>
            {initialTopContent}
            <p className="text-sm text-gray-600">
              <span className="text-red-500">*</span> {blok.label_hint_mandatory_field}
            </p>
          </div>
          <RHFForm {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="flex w-full  flex-col gap-4">
              <FormRow label={blok.label_email} htmlFor={"email"} required>
                <FormField
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          id="email"
                          type="email"
                          placeholder={blok?.label_email}
                          disabled={isPending}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormRow>
              <div className="flex justify-end gap-4">
                <Button type="reset" variant="outline" onClick={() => router.back()} disabled={isPending}>
                  {blok?.label_cancel}
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && <LoaderCircle className="mr-2 size-4 motion-safe:animate-spin" />}
                  {blok?.label_reset_password}
                </Button>
              </div>
            </form>
          </RHFForm>
          {initialBottomContent}
        </div>
      </div>
    </div>
  )
}
